// ─────────────────────────────────────────────────────────────────────────
// Onboarding state — backend persistence for the welcome modal + tour.
//
// Shape mirrors backend/cima/src/routes/onboarding.ts:
//   welcome_seen_at  — ISO timestamp the welcome modal was first shown
//   tour_progress    — per-track status map
//   completed_at     — set when the user marks the whole flow done
//   auto_marked      — true if back-fill migration auto-completed them
// ─────────────────────────────────────────────────────────────────────────

import { API_BASE, getApiHeaders } from './apiClient';

export type TourTrackStatus = 'in_progress' | 'completed' | 'skipped';

export interface OnboardingState {
  welcome_seen_at?: string | null;
  tour_progress?:   Record<string, TourTrackStatus>;
  completed_at?:    string | null;
  auto_marked?:     boolean;
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res  = await fetch(`${API_BASE}${path}`, {
    headers: { ...getApiHeaders(), ...options?.headers },
    ...options,
  });
  const text = await res.text();
  let data: any;
  try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data as T;
}

export async function getOnboardingState(): Promise<OnboardingState> {
  const data = await apiFetch<{ state: OnboardingState }>('/cima/onboarding');
  return data.state || {};
}

export async function patchOnboardingState(patch: Partial<OnboardingState>): Promise<OnboardingState> {
  const data = await apiFetch<{ state: OnboardingState }>('/cima/onboarding', {
    method: 'PATCH',
    body:   JSON.stringify(patch),
  });
  return data.state || {};
}

export async function resetOnboardingState(): Promise<OnboardingState> {
  const data = await apiFetch<{ state: OnboardingState }>('/cima/onboarding/reset', {
    method: 'POST',
  });
  return data.state || {};
}
